<template>
    <div class="markets">
        <div v-if="showDisplayTypeName" class="markets-title">
            {{ title }}
        </div>
        <div class="markets-block">
            <div
                v-for="market in markets"
                v-bind:key="market.matchMarketId"
                class="market-block"
                v-bind:style="{
                    'min-height': getMinHeight(market),
                }"
            >
                <Market
                    v-bind:market="market"
                    v-bind:match="match"
                    v-bind:displaySection="displaySection"
                />
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { computed, toRefs } from 'vue';
    import Market from '@/components/betBuilder/Market.vue';
    import type { IMatch } from '@/interface/IMatch';
    import type { IBetBuilderMarket } from '@/interface/betBuilder';
    import { BetBuilderDisplayType, DisplaySection } from '@/interface/enum';
    import { getMarketRule } from '@/libraries/oddsApi/betBuilderMarketRule';

    const props = withDefaults(defineProps<{
        markets: IBetBuilderMarket[];
        showDisplayTypeName?: boolean;
        showStatTypeInDisplayTypeName?: boolean;
        match: IMatch;
        displaySection?: DisplaySection;
    }>(), {
        showDisplayTypeName: false,
        showStatTypeInDisplayTypeName: false,
        displaySection: DisplaySection.Unknown,
    });

    const { markets } = toRefs(props);
    const displayType = computed(() => markets.value[0].displayType);
    const marketRule = computed(() => getMarketRule(displayType.value));
    const title = computed(() => (props.showStatTypeInDisplayTypeName
        ? marketRule.value.marketTypeWithStatTypeDisplay(displayType.value)
        : marketRule.value.marketTypeDisplay()));

    const minMarketCount: Partial<Record<BetBuilderDisplayType, number>> = {
        [BetBuilderDisplayType.OverUnder]: 2,
        [BetBuilderDisplayType._1X2]: 1,
    };

    function getMinHeight(market: IBetBuilderMarket) {
        const oddsHeight = 3.8;
        const bottomPadding = 0.6;
        const count = market.selections.length === 0 ? minMarketCount[market.displayType] ?? 0 : 0;
        return `${count * (oddsHeight + bottomPadding)}rem`;
    }
</script>

<style lang="scss" scoped>
.markets {
    &-title {
        padding: 0.5rem 1rem;
        background-color: $gray-2;
        color: $gray-1;
        font-weight: bold;
        text-align: center;

        .svgIcon {
            width: 1.2em;
            height: 1.2em;
            transform: translateY(2px);
        }
    }

    &-block {
        padding: 0.9rem;
    }
}

.market-block {
    display: flex;
    overflow: scroll;
    justify-content: flex-start;
}

._isLive .markets-title {
    background: $red-bg;

    .svgIcon {
        fill: #f05a28;
    }
}
</style>
